import { useController, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLockValidation, useDialogPortal } from "@/hooks";
import { LOCK_DURATION_KEY, TOKEN_IDS } from "@/types";
import { ALLOWED_LOCK_DURATION_VALUES } from "@/config";

interface UseChangeDurationFormProps {
    existingDuration: number;
    existingLockAmount: number;
    selectedDuration?: number;
}

type ChangeDurationFormValues = {
    [LOCK_DURATION_KEY]: number;
};

export function useChangeDurationForm({
    existingDuration,
    existingLockAmount,
    selectedDuration,
}: UseChangeDurationFormProps) {
    const { openChangeDurationTransactionDialog } = useDialogPortal();
    const lockSchema = useLockValidation();

    const form = useForm<ChangeDurationFormValues>({
        resolver: yupResolver(lockSchema),
        defaultValues: {
            [LOCK_DURATION_KEY]:
                selectedDuration &&
                selectedDuration >= existingDuration &&
                ALLOWED_LOCK_DURATION_VALUES.includes(selectedDuration)
                    ? selectedDuration
                    : existingDuration,
        },
    });

    const {
        field: duration,
        fieldState: { error: durationError },
    } = useController({
        name: LOCK_DURATION_KEY,
        control: form.control,
    });

    const submitChangeDuration = form.handleSubmit(() => {
        const newDuration = Number(duration.value);
        if (newDuration <= existingDuration) {
            form.setError(LOCK_DURATION_KEY, {
                type: "validate",
                message: "Select a longer unlock duration.",
            });
            return;
        }

        form.clearErrors(LOCK_DURATION_KEY);

        openChangeDurationTransactionDialog({
            existingDuration,
            lockAmount: existingLockAmount,
            newDuration,
            lockTokenId: TOKEN_IDS.IUSD,
        });
    });

    return {
        duration,
        durationError,
        submitChangeDuration,
        isLoading: false,
    };
}
